import React, { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";

import ImageOutlinedIcon from "@mui/icons-material/ImageOutlined";
import {
  Box,
  Button,
  CircularProgress,
  Drawer,
  InputAdornment,
  Slider,
  TextField,
  Typography
} from "@mui/material";
import { SimpleHead } from "components/Simple-head/SimpleHead";
import { debounce } from "lodash";

import { useReportsActions } from "store";
import type { SearchImageResult } from "store/types";

const DEFAULT_OPACITY = 100;

interface BackgroundDrawerProps {
  open: boolean;
  currentBackgroundUrl: string;
  currentOpacity: number;
  onClose: () => void;
  onApply: (url: string, opacity: number, file: File | null) => void;
  onRemove: () => void;
}

export const BackgroundDrawer: React.FC<BackgroundDrawerProps> = ({
  open,
  currentBackgroundUrl,
  currentOpacity,
  onClose,
  onApply,
  onRemove
}) => {
  const { searchBackgroundImagesAction } = useReportsActions();

  const [backgroundUrl, setBackgroundUrl] = useState(currentBackgroundUrl || "");
  const [backgroundOpacity, setBackgroundOpacity] = useState(currentOpacity || DEFAULT_OPACITY);
  const [backgroundFile, setBackgroundFile] = useState<File | null>(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState<SearchImageResult[]>([]);
  const [searchLoading, setSearchLoading] = useState(false);

  // Reset to the currently applied background each time the drawer opens.
  useEffect(() => {
    if (open) {
      setBackgroundUrl(currentBackgroundUrl || "");
      setBackgroundOpacity(currentOpacity || DEFAULT_OPACITY);
      setBackgroundFile(null);
    }
  }, [open, currentBackgroundUrl, currentOpacity]);

  const runSearch = useCallback(
    async (query: string) => {
      const trimmed = query.trim();
      if (!trimmed) {
        setSearchResults([]);
        setSearchLoading(false);
        return;
      }
      try {
        const results = await searchBackgroundImagesAction(trimmed);
        setSearchResults(results ?? []);
      } catch (error) {
        console.error("Failed to search images:", error);
        toast.error("Failed to search images");
        setSearchResults([]);
      } finally {
        setSearchLoading(false);
      }
    },
    [searchBackgroundImagesAction]
  );

  const debouncedSearch = useMemo(() => debounce(runSearch, 500), [runSearch]);

  // Cancel any in-flight debounce on unmount.
  useEffect(() => () => debouncedSearch.cancel(), [debouncedSearch]);

  const handleQueryChange = (value: string) => {
    setSearchQuery(value);
    if (value.trim()) {
      setSearchLoading(true);
      debouncedSearch(value);
    } else {
      debouncedSearch.cancel();
      setSearchLoading(false);
      setSearchResults([]);
    }
  };

  const handleSelectImage = useCallback((imageUrl: string) => {
    setBackgroundUrl(imageUrl);
    setBackgroundFile(null); // Clear any uploaded file when picking from the library.
  }, []);

  const handleUpload = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setBackgroundFile(file);
    const reader = new FileReader();
    reader.onload = (event) => setBackgroundUrl(event.target?.result as string);
    reader.readAsDataURL(file);
  }, []);

  const handleApply = useCallback(() => {
    onApply(backgroundUrl, backgroundOpacity, backgroundFile);
  }, [backgroundUrl, backgroundOpacity, backgroundFile, onApply]);

  return (
    <Drawer
      open={open}
      onClose={onClose}
      sx={{
        ".MuiPaper-root": {
          width: "430px",
          borderTopRightRadius: "25px",
          borderBottomRightRadius: "25px",
          display: "flex",
          flexDirection: "column"
        }
      }}
    >
      <SimpleHead onIconClick={onClose}>Background Image</SimpleHead>

      <Box sx={{ flex: 1, overflow: "auto", p: "24px 30px", display: "flex", flexDirection: "column", gap: "24px" }}>
        <Typography variant="body2" sx={{ color: "text.secondary" }}>
          Applied to every content, header and section-break page. Title pages keep their own layout.
        </Typography>

        {/* Search */}
        <TextField
          fullWidth
          label="Search images"
          placeholder="e.g. forest, skyline, abstract"
          value={searchQuery}
          onChange={(e) => handleQueryChange(e.target.value)}
          InputProps={{
            endAdornment: searchLoading ? (
              <InputAdornment position="end">
                <CircularProgress size={18} />
              </InputAdornment>
            ) : undefined
          }}
        />

        {!!searchResults.length && (
          <Box sx={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: "10px" }}>
            {searchResults.map((image) => {
              const selected = backgroundUrl === image.regular;
              return (
                <Box
                  key={image.thumb}
                  onClick={() => handleSelectImage(image.regular)}
                  sx={{
                    width: "100%",
                    aspectRatio: "1",
                    borderRadius: "8px",
                    overflow: "hidden",
                    cursor: "pointer",
                    border: "2px solid",
                    borderColor: selected ? "primary.main" : "divider",
                    transition: "all 0.15s ease-in-out",
                    "&:hover": { borderColor: "primary.light" }
                  }}
                >
                  <img
                    src={image.thumb}
                    alt="Background option"
                    style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }}
                  />
                </Box>
              );
            })}
          </Box>
        )}

        {/* Upload */}
        <Button variant="outlined" component="label" fullWidth startIcon={<ImageOutlinedIcon />}>
          Upload from computer
          <input type="file" hidden accept="image/*" onChange={handleUpload} />
        </Button>

        {/* Preview */}
        {backgroundUrl && (
          <Box>
            <Typography variant="body2" sx={{ color: "text.secondary", mb: "8px" }}>
              Preview
            </Typography>
            <Box
              sx={{
                width: "100%",
                height: 150,
                borderRadius: "8px",
                overflow: "hidden",
                border: "1px solid",
                borderColor: "divider",
                backgroundImage: `url(${backgroundUrl})`,
                backgroundSize: "cover",
                backgroundPosition: "center",
                opacity: backgroundOpacity / 100
              }}
            />
          </Box>
        )}

        {/* Opacity */}
        <Box>
          <Typography variant="body2" sx={{ color: "text.secondary", mb: "4px" }}>
            Opacity: {backgroundOpacity}%
          </Typography>
          <Slider
            value={backgroundOpacity}
            onChange={(_, value) => setBackgroundOpacity(value as number)}
            min={10}
            max={100}
            step={5}
            valueLabelDisplay="auto"
            valueLabelFormat={(value) => `${value}%`}
          />
        </Box>
      </Box>

      {/* Footer */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: currentBackgroundUrl ? "space-between" : "flex-end",
          gap: "16px",
          p: "20px 30px",
          boxShadow: "0 -4px 13px 0 rgba(10, 49, 66, 0.2)"
        }}
      >
        {currentBackgroundUrl && (
          <Button variant="text" color="error" onClick={onRemove}>
            Remove
          </Button>
        )}
        <Box sx={{ display: "flex", gap: "12px" }}>
          <Button variant="outlined" onClick={onClose}>
            Cancel
          </Button>
          <Button variant="contained" onClick={handleApply} disabled={!backgroundUrl}>
            Apply
          </Button>
        </Box>
      </Box>
    </Drawer>
  );
};
